import { z } from 'zod';
import {
    type CloverBrowserTokenRecordingGatewayResponse,
    type CloverBrowserTokenRecordingGatewayResponseV2,
    type CloverRecordTokenGatewayResponse,
    type CloverRecordTokenNormalizedResult,
    type CloverRecordTokenRequest,
    type CloverRecordTokenResponse,
} from '../../../types/ipg/clover/clover-token-recording.types';
import { CloverGatewayId } from '../../../types/ipg/clover/clover-system-constants';
import { CloverTokenizationResultType } from '../../../types/ipg/clover/clover-tokenization.types';
import {
    CloverEcommerceTokenizationSuccessResponseSchema,
    CloverEcommerceTokenizationProviderResponseSchema,
    CloverStoredCardMetadataTokenizationResultSchema,
    CloverTokenizationTokenSchema,
    CloverTokenizedCardMetadataSchema,
} from './clover-tokenization.schemas';
import { CardTypeSchema, CustomerDetailsSchema } from '../../samba-payment.schemas';
import { StoredMaskedPanSchema } from '../../masked_pan.schemas';

export const CloverRecordTokenUuidSchema: z.ZodString = z.string().uuid();

export const CloverBrowserTokenRecordingGatewayResponseSchema: z.ZodObject<{
    result_type: z.ZodLiteral<typeof CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION>;
    token_response: typeof CloverEcommerceTokenizationSuccessResponseSchema;
    card_metadata: typeof CloverTokenizedCardMetadataSchema;
}> = z
    .object({
        result_type: z.literal(CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION),
        token_response: CloverEcommerceTokenizationSuccessResponseSchema,
        card_metadata: CloverTokenizedCardMetadataSchema,
    })
    .strict() satisfies z.ZodType<CloverBrowserTokenRecordingGatewayResponse>;

export const CloverBrowserTokenRecordingGatewayResponseV2Schema = z
    .object({
        result_type: z.literal(CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION_V2),
        provider_response: CloverEcommerceTokenizationProviderResponseSchema,
        token: CloverTokenizationTokenSchema,
        card_metadata: CloverTokenizedCardMetadataSchema,
    })
    .strict()
    .superRefine((value, context) => {
        const providerToken = value.provider_response.token ?? value.provider_response.id;
        if (providerToken !== value.token) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Clover tokenization result does not match its provider response.',
                path: ['token'],
            });
        }
    }) satisfies z.ZodType<CloverBrowserTokenRecordingGatewayResponseV2>;

export const CloverRecordTokenGatewayResponseSchema: z.ZodType<CloverRecordTokenGatewayResponse> =
    z.discriminatedUnion('result_type', [
        CloverBrowserTokenRecordingGatewayResponseSchema,
        CloverBrowserTokenRecordingGatewayResponseV2Schema,
        CloverStoredCardMetadataTokenizationResultSchema,
    ]);

export const CloverRecordTokenRequestSchema: z.ZodObject<{
    terminal_device_uuid: typeof CloverRecordTokenUuidSchema;
    card_type: typeof CardTypeSchema;
    card_brand: z.ZodString;
    masked_pan: typeof StoredMaskedPanSchema;
    cardholder_name: z.ZodString;
    gateway_response: typeof CloverRecordTokenGatewayResponseSchema;
    customer_details: typeof CustomerDetailsSchema;
}> = z
    .object({
        terminal_device_uuid: CloverRecordTokenUuidSchema,
        card_type: CardTypeSchema,
        card_brand: z.string().min(1),
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: z.string().min(1),
        gateway_response: CloverRecordTokenGatewayResponseSchema,
        customer_details: CustomerDetailsSchema,
    })
    .strict() satisfies z.ZodType<CloverRecordTokenRequest>;

export const CloverRecordTokenResponseSchema: z.ZodObject<{
    success: z.ZodLiteral<true>;
    payment_method_token_uuid: typeof CloverRecordTokenUuidSchema;
}> = z
    .object({
        success: z.literal(true),
        payment_method_token_uuid: CloverRecordTokenUuidSchema,
    })
    .strict() satisfies z.ZodType<CloverRecordTokenResponse>;

export const CloverRecordTokenNormalizedResultSchema: z.ZodObject<{
    gateway_id: z.ZodLiteral<typeof CloverGatewayId>;
    gateway_token: typeof CloverTokenizationTokenSchema;
    card_type: typeof CardTypeSchema;
    card_brand: z.ZodString;
    masked_pan: typeof StoredMaskedPanSchema;
    cardholder_name: z.ZodString;
}> = z
    .object({
        gateway_id: z.literal(CloverGatewayId),
        gateway_token: CloverTokenizationTokenSchema,
        card_type: CardTypeSchema,
        card_brand: z.string().min(1),
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: z.string().min(1),
    })
    .strict() satisfies z.ZodType<CloverRecordTokenNormalizedResult>;
